import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

/**
 * Shared layout for BuildOnce Studio legal pages.
 * Renders a hero, the effective-date strip, and structured sections.
 *
 * sections: Array<{
 *   heading: string,
 *   paragraphs?: string[],
 *   bullets?: string[],
 *   note?: string,           // small italic note shown at end of section
 *   subSections?: Array<{ heading: string, paragraphs?: string[], bullets?: string[] }>
 * }>
 */
export default function LegalLayout({
  eyebrow,
  title,
  intro,
  effective = "Effective from the date of first purchase",
  meta = "BuildOnce Studio · UEN: 53518159X · Singapore",
  sections = [],
  testId,
}) {
  useEffect(() => {
    document.title = `${title} · BuildOnce Studio`;
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [title]);

  return (
    <div data-testid={testId}>
      <section className="border-b border-bos-ice">
        <div className="mx-auto max-w-[820px] px-6 lg:px-10 pt-24 lg:pt-28 pb-12">
          <Link
            to="/"
            data-testid="legal-back-home"
            className="inline-flex items-center gap-2 text-bos-primary bos-link-underline text-[13px]"
          >
            <ArrowLeft size={13} /> Back to BuildOnce Studio
          </Link>

          <p className="bos-eyebrow mt-8">{eyebrow}</p>
          <h1 className="mt-4 text-3xl sm:text-4xl lg:text-[48px] font-medium text-bos-navy tracking-tight leading-[1.1]">
            {title}
          </h1>
          {intro && (
            <p className="mt-6 text-[16px] text-bos-charcoal/85 leading-[1.75] max-w-2xl">
              {intro}
            </p>
          )}

          <div className="mt-8 bg-bos-ice/70 border border-bos-light/60 rounded-sm px-5 py-4 text-[12px] text-bos-charcoal/80 leading-[1.7]">
            <p>{effective}</p>
            <p className="mt-1 text-bos-charcoal/60">{meta}</p>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-[820px] px-6 lg:px-10 py-14 lg:py-20">
        <div className="space-y-12">
          {sections.map((s, i) => (
            <LegalSection key={`${title}-${i}`} section={s} index={i} />
          ))}
        </div>

        <div className="mt-20 border-t border-bos-ice pt-10 text-[13px] text-bos-charcoal/70 leading-[1.75]">
          <p>
            Questions about this policy? Write to{" "}
            <a
              href="mailto:info@buildoncestudio.com"
              data-testid="legal-email-link"
              className="text-bos-primary bos-link-underline"
            >
              info@buildoncestudio.com
            </a>
            . We respond within two business days.
          </p>
          <div className="mt-6 flex flex-wrap gap-x-6 gap-y-2 text-[12px] tracking-[0.04em] text-bos-charcoal/60">
            <Link to="/privacy-policy" className="hover:text-bos-navy" data-testid="legal-xref-privacy">Privacy Policy</Link>
            <Link to="/terms-and-conditions" className="hover:text-bos-navy" data-testid="legal-xref-terms">Terms and Conditions</Link>
            <Link to="/refund-policy" className="hover:text-bos-navy" data-testid="legal-xref-refund">Refund Policy</Link>
            <Link to="/disclaimer" className="hover:text-bos-navy" data-testid="legal-xref-disclaimer">Disclaimer</Link>
          </div>
        </div>
      </section>
    </div>
  );
}

const LegalSection = ({ section, index }) => (
  <section data-testid={`legal-section-${index}`} className="grid lg:grid-cols-[1fr_3fr] gap-6 lg:gap-10">
    <div className="text-[11px] tracking-[0.18em] uppercase text-bos-mid pt-1">
      {String(index + 1).padStart(2, "0")} · {section.heading}
    </div>
    <div>
      {section.paragraphs?.map((p, j) => (
        <p key={j} className="text-[15px] text-bos-charcoal/90 leading-[1.85] mb-4 last:mb-0">
          {renderInlineEmphasis(p)}
        </p>
      ))}

      {section.bullets && (
        <ul className="mt-2 space-y-2.5">
          {section.bullets.map((b, j) => (
            <li
              key={j}
              className="pl-5 relative text-[15px] text-bos-charcoal/90 leading-[1.75] before:content-[''] before:absolute before:left-0 before:top-[0.78em] before:h-[5px] before:w-[5px] before:bg-bos-navy before:rounded-full"
            >
              {renderInlineEmphasis(b)}
            </li>
          ))}
        </ul>
      )}

      {section.subSections?.map((sub, j) => (
        <div key={j} className="mt-7">
          <h3 className="text-[14px] tracking-tight font-medium text-bos-navy mb-3">
            {sub.heading}
          </h3>
          {sub.paragraphs?.map((p, k) => (
            <p key={k} className="text-[15px] text-bos-charcoal/90 leading-[1.85] mb-3 last:mb-0">
              {renderInlineEmphasis(p)}
            </p>
          ))}
          {sub.bullets && (
            <ul className="mt-2 space-y-2.5">
              {sub.bullets.map((b, k) => (
                <li
                  key={k}
                  className="pl-5 relative text-[15px] text-bos-charcoal/90 leading-[1.75] before:content-[''] before:absolute before:left-0 before:top-[0.78em] before:h-[5px] before:w-[5px] before:bg-bos-navy before:rounded-full"
                >
                  {renderInlineEmphasis(b)}
                </li>
              ))}
            </ul>
          )}
        </div>
      ))}

      {section.note && (
        <p className="mt-5 text-[13px] text-bos-charcoal/65 italic leading-[1.7]">
          {section.note}
        </p>
      )}
    </div>
  </section>
);

// Lightweight inline emphasis: turn **bold** into <strong>, keep plain text otherwise.
const renderInlineEmphasis = (text) => {
  if (!text || !text.includes("**")) return text;
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((part, i) =>
    part.startsWith("**") && part.endsWith("**") ? (
      <strong key={i} className="text-bos-navy font-medium">
        {part.slice(2, -2)}
      </strong>
    ) : (
      <span key={i}>{part}</span>
    )
  );
};
